// B6 — cached per-rs registry lookups for the SNP editor's registry reference and the picker's
// evidence column. `unavailable` is never cached, so an outage heals on the next look.
import { fetchRegistryVariant } from "../../api/geneCodes/RegistryApi";
import { RegistryVariantLookup } from "../../types/geneCodes/RegistryTypes";

export const isCompleteRs = (value: string): boolean => /^rs\d+$/i.test(value.trim());

const cache = new Map<string, RegistryVariantLookup>();

/** rs is sent VERBATIM (the server lowercases); the cache folds case so RS123 and rs123 are one entry. */
export const lookupVariant = async (rs: string): Promise<RegistryVariantLookup> => {
	const key = rs.trim().toLowerCase();
	const hit = cache.get(key);
	if (hit) return hit;
	const lookup = await fetchRegistryVariant(rs.trim());
	if (lookup.status !== "unavailable") cache.set(key, lookup);
	return lookup;
};

/** The picker's page, deduped and pooled — 25 visible rows must not open 25 sockets at once. Keys are the caller's verbatim rs. */
export const lookupVariants = async (rsList: string[], poolSize = 6): Promise<Map<string, RegistryVariantLookup>> => {
	const distinct = Array.from(new Set(rsList.filter((rs) => isCompleteRs(rs))));
	const results = new Map<string, RegistryVariantLookup>();
	let next = 0;

	const worker = async () => {
		while (next < distinct.length) {
			const rs = distinct[next];
			next += 1;
			results.set(rs, await lookupVariant(rs));
		}
	};

	await Promise.all(Array.from({ length: Math.min(poolSize, distinct.length) }, worker));
	return results;
};

export const __resetVariantEvidenceCache = (): void => cache.clear();
